import { Component, LOCALE_ID, OnDestroy, OnInit, inject } from '@angular/core';
import { ReactiveFormsModule, Validators, FormBuilder, ValidatorFn } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule, Location } from '@angular/common';
import { CustomerService } from 'src/app/services/customer.service';
import { CustomerGroupService } from 'src/app/services/customergroup.service';
import { CustomerGroup } from 'src/app/common/CustomerGroup';
import { combineLatest, EMPTY, merge, Observable, Subscription, tap } from 'rxjs';
import { BankAccount } from 'src/app/common/BankAccount';
import { EntityTypeConstants } from 'src/app/common/EntityTypeConstants';
import { BankAccountValidatorService } from 'src/app/services/bank-account-validator.service';
import { CustomerNameValidatorService } from 'src/app/services/customer-name-validator.service';
import { InvoiceTypeValidatorService } from 'src/app/services/invoice-type-validator.service';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { InvoiceType } from 'src/app/common/InvoiceType';
import { ShopCommonService } from 'src/app/services/shop-common.service';
import { TabStripModule } from '@progress/kendo-angular-layout';
import { PermissionTableComponent } from '../permission-table/permission-table.component';
import { FilesComponent } from '../files/file-select.component';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { CountryModel } from '../../common/Country';
import { CountryService } from '../../services/country.service';
import { Customer } from '../../common/Customer';
import { FilePostDto } from '../../common/FilePostDto';
import { CallApiService } from '../../services/api-action.service';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { salutationCodeValidator, salutationOptionsFor } from '../../common/Salutation';
import { CountryItem, toCountryItems } from '../../common/country-name';
import { saveBlob } from 'src/app/common/browser-files';
import { FormRowComponent } from '../form-row/form-row.component';
import { submittable } from '../../common/server-field-errors';

/** The customer master-data form; the cross-field rules come in as `formValidators`. Its type is inferred from here (#199). */
function buildCustomerForm(fb: FormBuilder, customer: Customer, formValidators: ValidatorFn[]) {
  return fb.group(
    {
      customerNumber: [customer?.customerNumber, Validators.required],
      description: [customer?.description],
      fax: [customer?.fax],
      id: [customer?.id],
      firstName: [customer?.firstName, Validators.maxLength(100)],
      fullName: [customer?.fullName],
      internet: [customer?.internet, Validators.maxLength(500)],
      lastName: [customer?.lastName, Validators.maxLength(100)],
      customerGroupId: [customer?.customerGroupId, Validators.required],
      isRetailer: [customer?.isRetailer, Validators.required],
      smallBusinessP19UStG: [customer?.smallBusinessP19UStG],
      middleName: [customer?.middleName, Validators.maxLength(100)],
      phone: [customer?.phone, Validators.maxLength(100)],
      stateOrRegion: [customer?.stateOrRegion, Validators.maxLength(50)],
      // #233 (backend #736): required like the zip and city below it — all three are NOT
      // NULL on Customer, and an empty street now comes back as a coded 400 from
      // CustomerValidationCommand instead of a 500.
      street: [customer?.street, [Validators.required, Validators.maxLength(100)]],
      taxId: [customer?.taxId, Validators.maxLength(100)],
      // #525: normalized uppercase server-side; a value outside the dropdown (e.g. a
      // customer already set to an uncommon code via the API) is kept selectable-as-is.
      defaultCurrency: [customer?.defaultCurrency ?? 'EUR', Validators.required],
      selectedInvoiceType: [customer?.invoiceType, Validators.required],
      isActive: [customer?.isActive],
      isCustomSellerInfoActive: [customer?.isCustomSellerInfoActive],
      vatRegNo: [customer?.vatRegNo, Validators.maxLength(100)],
      zip: [customer?.zipCode, [Validators.required, Validators.maxLength(50)]],
      city: [customer?.city, [Validators.required, Validators.maxLength(100)]],
      countryId: [customer?.countryId, [Validators.required]],
      // Backend #670: the contact address is mailed and printed — 320 (the mail queue's
      // limit) and a real address, matching the backend validator.
      email: [customer?.email, [Validators.maxLength(320), Validators.email]],
      title: [customer?.title, Validators.maxLength(100)],
      // #165: the salutation („Herr", „Frau", „Firma") — not the postal address, which is
      // street/zip/city above.
      // backend #682: a code now, so the length rule is gone and salutationCodeValidator
      // refuses at the field what the backend would refuse as `salutationInvalid`.
      salutation: [customer?.salutation, [Validators.required, salutationCodeValidator]],
      companyName: [customer?.companyName, Validators.maxLength(100)],
      iban: [customer?.bankAccount?.iban],
      bankName: [customer?.bankAccount?.bankName, Validators.maxLength(100)],
      swiftCode: [customer?.bankAccount?.swiftCode, Validators.maxLength(50)],
    },
    {
      validators: formValidators,
    }
  );
}
type CustomerForm = ReturnType<typeof buildCustomerForm>;

@Component({
    imports: [CommonModule, ReactiveFormsModule, TabStripModule, KENDO_DROPDOWNS, PermissionTableComponent, FilesComponent, AsyncClickDirective, FormRowComponent],
    selector: 'app-customer-edit',
    templateUrl: './customer-edit.component.html'
})
export class CustomerEditComponent implements OnInit, OnDestroy {
  fb = inject(FormBuilder);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  customerService = inject(CustomerService);
  private customerGroupService = inject(CustomerGroupService);
  private location = inject(Location);
  private shopCommonService = inject(ShopCommonService);
  private countryService = inject(CountryService);
  private apiActionService = inject(CallApiService);
  private localeId = inject(LOCALE_ID);

  constructor() {
    this.customer = new Customer();
    this.customerForm = this.createForm(this.customer);
  }

  ngOnDestroy(): void {
    this.invoiceTypeAutoSetSubscription?.unsubscribe();
  }

  public faInfoCircle = faInfoCircle;
  public bankAccountValidator: BankAccountValidatorService = new BankAccountValidatorService();
  public customerNameValidator: CustomerNameValidatorService = new CustomerNameValidatorService();
  public invoiceTypeValidator: InvoiceTypeValidatorService = new InvoiceTypeValidatorService();
  public id: string;
  public customerForm: CustomerForm;
  public customer: Customer;
  public submitted = false;
  public referenceId: number;
  public entityType: string;
  public filesChanged: boolean = false;
  public fileTransactions: FilePostDto[] = [];
  // Re-created on every form build, which is why it is unsubscribed by hand rather than tied to
  // the component's destruction.
  private invoiceTypeAutoSetSubscription: Subscription;
  public customerGroups: CustomerGroup[];
  public invoiceTypes: InvoiceType[];
  public selectedInvoiceType: InvoiceType;
  public countries: CountryModel[];
  /** #178: the same list, labelled from `code2` in the language on screen. */
  public countryItems: CountryItem[] = [];
  // Backend #525: the seller-level currency default every new listing adopts at create.
  // Common codes only — the backend accepts any well-formed ISO 4217 code, but a plain
  // dropdown keeps the input shape-safe (the API 400s a non-ISO shape).
  public currencyOptions: string[] = ['EUR', 'GBP', 'USD', 'CHF'];

  get f() {
    return this.customerForm.controls;
  }

  ngOnInit() {
    this.entityType = EntityTypeConstants.EntityTypeCustomer;
    this.customerGroups = [];
    this.id = this.activatedRoute.snapshot.paramMap.get('id');

    this.invoiceTypes = this.shopCommonService.getInvoiceTypes();

    combineLatest({
      customerGroups: this.customerGroupService.get(),
    }).subscribe((r) => {
      this.customerGroups = r.customerGroups;
    });

    this.countryService.get().subscribe((res: CountryModel[]) => {
      this.countries = res;
      this.countryItems = toCountryItems(res, this.localeId);
    });

    if (this.id) {
      this.customerService.getById(this.id).subscribe((c) => {
        this.customer = c;
        this.fileTransactions = (c.files ?? []).map((f) => ({
          filePayload: f,
          isCreated: false,
          isDeleted: false,
        }));
        this.customerForm = this.createForm(this.customer);
      });
    }
    this.selectedInvoiceType = this.invoiceTypes.find((it) => it.Id == this.customer.invoiceType);
  }

  /** backend #682: the Anrede picker. Computed once per form, not per change-detection run —
   *  a legacy value adds an option and would otherwise hand the template a new array each pass. */
  public salutationOptions = salutationOptionsFor(null);

  createForm(customer: Customer): CustomerForm {
    // #525: a customer already set to an uncommon code (via the API) must stay selectable.
    if (customer?.defaultCurrency && !this.currencyOptions.includes(customer.defaultCurrency)) {
      this.currencyOptions = [...this.currencyOptions, customer.defaultCurrency];
    }
    // backend #682: the same courtesy for a salutation written before the codes existed.
    this.salutationOptions = salutationOptionsFor(customer?.salutation);
    const form = buildCustomerForm(this.fb, customer, [
      this.bankAccountValidator.BankAccountValidator(),
      this.customerNameValidator.CustomerNameValidator(),
      this.invoiceTypeValidator.InvoiceTypeValidator(),
    ]);
    this.wireInvoiceTypeAutoSet(form);
    return form;
  }

  // The backend allows exactly one invoice type per retailer/small-business
  // combination, so toggling either checkbox snaps the dropdown to the only
  // valid value. Data loaded in an invalid combination is NOT auto-corrected
  // (gross vs. net changes pricing semantics) — the validator blocks the save
  // until the user resolves it consciously.
  private wireInvoiceTypeAutoSet(form: CustomerForm): void {
    this.invoiceTypeAutoSetSubscription?.unsubscribe();
    this.invoiceTypeAutoSetSubscription = merge(
      form.controls.isRetailer.valueChanges,
      form.controls.smallBusinessP19UStG.valueChanges
    ).subscribe(() => {
      form
        .get('selectedInvoiceType')
        .setValue(
          InvoiceTypeValidatorService.deriveInvoiceType(
            !!form.controls.isRetailer.value,
            !!form.controls.smallBusinessP19UStG.value
          )
        );
    });
  }

  submitForm(): Observable<unknown> {
    if (!submittable(this.customerForm)) {
      return EMPTY;
    }
      this.customer.customerNumber = this.customerForm.controls.customerNumber.value;
      this.customer.description = this.customerForm.controls.description.value;
      this.customer.fax = this.customerForm.controls.fax.value;
      this.customer.id = this.customerForm.controls.id.value;
      this.customer.firstName = this.customerForm.controls.firstName.value;
      this.customer.lastName = this.customerForm.controls.lastName.value;
      this.customer.customerGroupId = this.customerForm.controls.customerGroupId.value;
      this.customer.isRetailer = this.customerForm.controls.isRetailer.value;
      this.customer.smallBusinessP19UStG = this.customerForm.controls.smallBusinessP19UStG.value;
      this.customer.middleName = this.customerForm.controls.middleName.value;
      this.customer.phone = this.customerForm.controls.phone.value;
      this.customer.stateOrRegion = this.customerForm.controls.stateOrRegion.value;
      this.customer.street = this.customerForm.controls.street.value;
      this.customer.taxId = this.customerForm.controls.taxId.value;
      this.customer.defaultCurrency = this.customerForm.controls.defaultCurrency.value;
      this.customer.invoiceType = this.customerForm.controls.selectedInvoiceType.value;
      this.customer.isActive = this.customerForm.controls.isActive.value;
      this.customer.isCustomSellerInfoActive = this.customerForm.get(
        'isCustomSellerInfoActive'
      ).value;
      this.customer.vatRegNo = this.customerForm.controls.vatRegNo.value;
      this.customer.zipCode = this.customerForm.controls.zip.value;
      this.customer.city = this.customerForm.controls.city.value;
      this.customer.countryId = this.customerForm.controls.countryId.value;
      this.customer.email = this.customerForm.controls.email.value;
      this.customer.title = this.customerForm.controls.title.value;
      this.customer.salutation = this.customerForm.controls.salutation.value;
      this.customer.companyName = this.customerForm.controls.companyName.value;

      if (
        this.customerForm.controls.iban.value ||
        this.customerForm.controls.swiftCode.value ||
        this.customerForm.controls.bankName.value
      ) {
        // Start from the existing bank account so fields the form doesn't expose
        // (isActive, accountNumber, description, id) are preserved, and only the
        // editable fields are overwritten. customerId is required by the backend's
        // BankAccountModel — omitting it makes the whole customer update fail to
        // deserialize (400).
        const bankAccount = this.customer.bankAccount ?? new BankAccount();
        bankAccount.customerId = this.customer.id;
        bankAccount.iban = this.customerForm.controls.iban.value;
        bankAccount.swiftCode = this.customerForm.controls.swiftCode.value;
        bankAccount.bankName = this.customerForm.controls.bankName.value;
        this.customer.bankAccount = bankAccount;
      } else {
        this.customer.bankAccount = null;
      }

      this.submitted = true;

      const isUpdate = !!this.customer.id;
      return this.apiActionService
        .callWithToasts$(
          isUpdate
            ? this.customerService.updateMultipart(this.customer.id, this.customer, this.fileTransactions)
            : this.customerService.create(this.customer),
          isUpdate ? $localize`Kunde wurde aktualisiert.` : $localize`Kunde wurde erstellt.`,
          isUpdate
            ? $localize`Beim Aktualisieren des Kunden ist ein Fehler aufgetreten.`
            : $localize`Beim Erstellen des Kunden ist ein Fehler aufgetreten.`,
          // #178: coded field errors land at their control (see welcome-app-data).
          this.customerForm
        )
        .pipe(
          tap(() => {
            if (isUpdate) {
              this.filesChanged = false;
            }
            this.router.navigateByUrl('shop-customer');
          })
        );
  }

  onBackClicked() {
    this.location.back();
  }

  onFileAdded(_filePostDto: FilePostDto) {
    this.filesChanged = true;
  }

  onFileRemoved(_filePostDto: FilePostDto) {
    this.filesChanged = true;
  }

  onFileDownload(filePostDto: FilePostDto) {
    if (!this.customer.id || !filePostDto.filePayload?.id) {
      return;
    }
    this.customerService
      .downloadFile(this.customer.id, filePostDto.filePayload.id)
      .subscribe((blob) => saveBlob(blob, filePostDto.filePayload.originalFileName));
  }
}
